import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, Users, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SettingsLayout } from './SettingsLayout';
import { getMemberships } from '@/api/settings';

export function MembershipsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['memberships'],
    queryFn: getMemberships,
  });

  if (isLoading) {
    return (
      <SettingsLayout>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SettingsLayout>
    );
  }

  const memberships = data?.memberships || [];

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'administrator':
        return 'bg-purple-100 text-purple-700';
      case 'developer':
        return 'bg-blue-100 text-blue-700';
      case 'readonly':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <SettingsLayout>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Project Memberships
          </CardTitle>
          <CardDescription>
            View all projects you are a member of.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {memberships.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">
                You are not a member of any projects yet.
              </p>
              <Button asChild>
                <Link to="/projects">View Projects</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {memberships.map((membership) => (
                <div 
                  key={membership.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Users className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{membership.project_name}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className={getRoleBadgeColor(membership.role)}>
                          {membership.role.charAt(0).toUpperCase() + membership.role.slice(1)}
                        </Badge>
                        {!membership.activated && (
                          <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                            Pending
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/project/${membership.project_id}/deployments`}>
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </SettingsLayout>
  );
}
